import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import classes from "../homePageNew.module.css";

const TOOLTIP_WIDTH = 240;

/**
 * Generic "value +N" chip for a multi-select field (market/persona/llm/
 * category, or any future module's own multi-value field) — shows the first
 * value plus an overflow count when there's more than one, and reveals every
 * value in a hover tooltip. Takes a plain string[] so it never needs to know
 * which field or module it's rendering.
 *
 * The tooltip is rendered through a portal into document.body rather than
 * as a normal absolutely-positioned child: the pill sits inside a list card
 * inside .mdListScroll/.cardPills, both of which clip overflowing content
 * (scroll clipping + ellipsis containment), so an in-place tooltip was
 * getting silently clipped and never showing. Portaling escapes that
 * ancestor chain; position is computed from the pill's own bounding box on
 * hover instead.
 *
 * Also fires for a SINGLE value when it's long enough to be CSS-ellipsized
 * (detected via scrollWidth > clientWidth at hover time) — a lone value can
 * get cut off just as easily as an overflowing list, and should still be
 * readable on hover rather than only once there are 2+ values.
 */
export default function OverflowPill({ values, label, mono = false, className = "" }) {
  const list = (Array.isArray(values) ? values : values ? [values] : []).filter(Boolean);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const anchorRef = useRef(null);
  const mainRef = useRef(null);

  if (list.length === 0) return null;
  const extra = list.length - 1;

  const showTooltip = () => {
    if (!anchorRef.current) return;
    const isTruncated = mainRef.current && mainRef.current.scrollWidth > mainRef.current.clientWidth + 1;
    if (extra <= 0 && !isTruncated) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({
      top: rect.top,
      left: Math.min(rect.left, window.innerWidth - TOOLTIP_WIDTH - 12),
    });
    setOpen(true);
  };

  return (
    <span className={classes.overflowPill} ref={anchorRef} onMouseEnter={showTooltip} onMouseLeave={() => setOpen(false)}>
      <span className={`${classes.overflowPillMain} ${mono ? classes.overflowPillMono : ""} ${className}`}>
        <span ref={mainRef} className={classes.overflowPillText}>
          {list[0]}
        </span>
        {extra > 0 && <b>{`+${extra}`}</b>}
      </span>
      {open &&
        createPortal(
          <span
            className={classes.overflowTooltipPortal}
            style={{ top: pos.top, left: pos.left }}
          >
            {extra > 0 && (
              <span className={classes.overflowTooltipHead}>
                {label ? `${label} (${list.length})` : `${list.length} selected`}
              </span>
            )}
            <span className={classes.overflowTooltipList}>
              {list.map((v, i) => (
                <span key={i}>{v}</span>
              ))}
            </span>
          </span>,
          document.body
        )}
    </span>
  );
}
